import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useContext, useEffect, useState, memo } from "react";
import { AuthContext } from "./context/AuthContext.jsx";
import { AuthProvider } from "./context/AuthContextDefinition.jsx";
import Sidebar from "./components/Sidebar";
import Dashboard from "./components/Dashboard";
import POS from "./components/POS";
import Inventory from "./components/Inventory";
import Accounting from "./components/Accounting";
import Attendance from "./components/Attendance";
import Payroll from "./components/Payroll";
import ReportsAnalytics from "./components/ReportsAnalytics";
import Customer from "./components/Customer";
import CommsMarketing from "./components/CommsMarketing";
import Login from "./components/Login";
import KioskLogin from "./components/kiosk/KioskLogin";
import KioskNavBar from "./components/kiosk/KioskNavBar";
import POSSection from "./components/kiosk/POSSection";
import InventoryCheckout from "./components/kiosk/InventoryCheckout";
import Signage from "./components/Signage";
import Recruitment from "./components/Recruitment";
import SignageDisplay from "./components/SignageDisplay";
import React from "react";
import log from 'loglevel';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Menu } from 'lucide-react';

log.setLevel(process.env.NODE_ENV === 'production' ? 'error' : 'debug');

class ErrorBoundary extends React.Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-red-500 text-center">
            <h1 className="text-2xl font-bold font-raleway">Error</h1>
            <p className="font-libre">Something went wrong: {this.state.error?.message || "Unknown error"}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 tua font-raleway"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const ADMIN_ROLES = ['admin'];
const FINANCE_ROLES = ['admin', 'accountant'];
const KIOSK_ROLES = ['cashier', 'customer_service', 'manager'];

const ProtectedRoute = memo(({ children, isKiosk, allowAccountant, requireCustomerServiceOrAdmin, requireManagerOrAdmin }) => {
  const { user, loading } = useContext(AuthContext);
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen font-libre">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!user) {
    log.debug("ProtectedRoute: No user, redirecting to", isKiosk ? "/kiosk/login" : "/login");
    return <Navigate to={isKiosk ? "/kiosk/login" : "/login"} state={{ from: location }} replace />;
  }

  if (!isKiosk) {
    const allowed = allowAccountant ? FINANCE_ROLES : ADMIN_ROLES;
    if (!allowed.includes(user.role)) {
      log.warn("ProtectedRoute: Admin route denied for role:", user.role);
      return <Navigate to="/kiosk/login" replace />;
    }
  }

  if (isKiosk && !KIOSK_ROLES.includes(user.role)) {
    log.warn("ProtectedRoute: Kiosk route denied for role:", user.role);
    return <Navigate to="/login" replace />;
  }

  if (requireCustomerServiceOrAdmin && !['customer_service', 'manager', 'admin'].includes(user.role)) {
    return <Navigate to="/kiosk/pos/salon" replace />;
  }

  if (requireManagerOrAdmin && !['manager', 'admin'].includes(user.role)) {
    return <Navigate to="/kiosk/pos/salon" replace />;
  }

  log.debug("ProtectedRoute: Access granted for role", user.role, "to", location.pathname);
  return children;
});

const AppContent = memo(() => {
  const { user, loading } = useContext(AuthContext);
  const [showSidebar, setShowSidebar] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem('ellas_sidebar_collapsed') === 'true'
  );
  const location = useLocation();

  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('ellas_sidebar_collapsed', String(next));
      return next;
    });
  };

  useEffect(() => {
    const isKioskRoute = location.pathname.startsWith("/kiosk");
    const isSignageDisplayRoute = location.pathname.startsWith("/signage-display");
    const shouldShow = user && !isKioskRoute && !isSignageDisplayRoute && !["/login", "/kiosk/login"].includes(location.pathname);
    setShowSidebar(shouldShow);
    setSidebarOpen(false); // close drawer on every route change
    log.debug("AppContent: path", location.pathname, "showSidebar:", shouldShow);
  }, [location.pathname, user, loading]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen w-full font-libre">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen w-full">
      {showSidebar && (
        <Sidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          collapsed={sidebarCollapsed}
          onToggleCollapse={toggleSidebarCollapsed}
        />
      )}

      <main
        className={`flex-1 w-full transition-all duration-300 ${
          showSidebar ? (sidebarCollapsed ? "md:ml-16" : "md:ml-64") : "ml-0"
        }`}
      >
        {/* Hamburger — mobile only, visible when sidebar drawer is closed */}
        {showSidebar && !sidebarOpen && (
          <button
            onClick={() => setSidebarOpen(true)}
            className="md:hidden fixed top-3 left-3 z-[70] bg-gray-800 text-white p-2 rounded-md shadow-lg"
            aria-label="Open menu"
          >
            <Menu size={20} />
          </button>
        )}

        <ErrorBoundary>
          <Routes>
            {/* ADMIN ROUTES */}
            <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/pos" element={<ProtectedRoute><POS /></ProtectedRoute>} />
            <Route path="/inventory" element={<ProtectedRoute><Inventory /></ProtectedRoute>} />
            <Route path="/customers" element={<ProtectedRoute><Customer /></ProtectedRoute>} />
            <Route path="/attendance" element={<ProtectedRoute><Attendance /></ProtectedRoute>} />
            <Route path="/reports" element={<ProtectedRoute><ReportsAnalytics /></ProtectedRoute>} />
            <Route path="/comms-marketing" element={<ProtectedRoute><CommsMarketing /></ProtectedRoute>} />
            <Route path="/signage" element={<ProtectedRoute><Signage /></ProtectedRoute>} />
            <Route path="/recruitment" element={<ProtectedRoute><Recruitment /></ProtectedRoute>} />

            {/* FINANCE ROUTES — admin + accountant */}
            <Route path="/accounting" element={<ProtectedRoute allowAccountant><Accounting /></ProtectedRoute>} />
            <Route path="/payroll" element={<ProtectedRoute allowAccountant><Payroll /></ProtectedRoute>} />

            {/* KIOSK ROUTES */}
            <Route path="/kiosk" element={<ProtectedRoute isKiosk><Navigate to="/kiosk/pos/salon" replace /></ProtectedRoute>} />
            <Route path="/kiosk/pos/:section" element={<ProtectedRoute isKiosk><POSSection /></ProtectedRoute>} />
            <Route path="/kiosk/inventory" element={<ProtectedRoute isKiosk><InventoryCheckout /></ProtectedRoute>} />
            <Route path="/kiosk/comms-marketing" element={<ProtectedRoute isKiosk requireCustomerServiceOrAdmin><CommsMarketing /></ProtectedRoute>} />
            <Route path="/kiosk/signage" element={<ProtectedRoute isKiosk requireCustomerServiceOrAdmin><Signage /></ProtectedRoute>} />
            <Route path="/kiosk/attendance" element={<ProtectedRoute isKiosk requireManagerOrAdmin><div className="min-h-screen bg-gray-50 p-4"><KioskNavBar /><Attendance allowedSections={['dashboard', 'holidayleave']} /></div></ProtectedRoute>} />
            <Route path="/kiosk/payroll" element={<ProtectedRoute isKiosk requireManagerOrAdmin><div className="min-h-screen bg-gray-50 p-4"><KioskNavBar /><Payroll /></div></ProtectedRoute>} />

            {/* PUBLIC TV DISPLAYS */}
            <Route path="/signage-display/gym" element={<SignageDisplay location="gym" />} />
            <Route path="/signage-display/salon" element={<SignageDisplay location="salon" />} />

            {/* LOGIN */}
            <Route path="/login" element={<Login />} />
            <Route path="/kiosk/login" element={<KioskLogin />} />

            {/* CATCH-ALL */}
            <Route
              path="*"
              element={
                user ? (
                  KIOSK_ROLES.includes(user.role) ? (
                    <Navigate to="/kiosk/pos/salon" replace />
                  ) : user.role === 'admin' ? (
                    <Navigate to="/dashboard" replace />
                  ) : user.role === 'accountant' ? (
                    <Navigate to="/accounting" replace />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
          </Routes>
        </ErrorBoundary>
      </main>

      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
    </div>
  );
});

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
